import { useState } from 'react';
import { useTranslation } from 'react-i18next';

const BookingSection = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    vehicle: '',
    date: '',
    duration: '',
    message: ''
  });

  const vehicles = [
    'Prado TX-L BVA',
    'Toyota Fortuner',
    'Jeep Wrangler',
    'Toyota Highlander XSE',
    'Four Runner',
    'Lexus RX 350',
    'Peugeot 5008 GT',
    'Cadillac Escalade',
    'Acura RDX 2021',
    'Toyota CHR',
    'Nissan Juke',
    'Toyota Starlet',
    'Pick-Up RAM',
    'Pick-Up Hilux Double Cabine',
    'Ford 250',
    'Dyna',
    'Hyap',
    'Fourgonnette frigorifique',
    'Camionette frigorifique'
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const message = `Bonjour NPA,

Je souhaite réserver un véhicule avec les détails suivants:

👤 Nom: ${formData.name}
📞 Téléphone: ${formData.phone}
📧 Email: ${formData.email}
🚗 Véhicule: ${formData.vehicle}
📅 Date: ${formData.date}
⏰ Durée: ${formData.duration}

Message supplémentaire:
${formData.message}

Merci de me contacter pour confirmer la disponibilité et les tarifs.`;

    const whatsappUrl = `https://wa.me/237693695377?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  return (
    <section id="contact" className="py-12 sm:py-16 lg:py-20 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-8 sm:mb-12 lg:mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 sm:mb-4">
            {t('booking.title')}
          </h2>
          <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto px-4">
            {t('booking.subtitle')}
          </p>
        </div>

        <div className="max-w-2xl mx-auto">
          <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-xl p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                  {t('booking.name')}
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-yellow-400 focus:border-transparent text-sm"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                  {t('booking.phone')}
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-yellow-400 focus:border-transparent text-sm"
                />
              </div>
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                {t('booking.email')}
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-yellow-400 focus:border-transparent text-sm"
              />
            </div>

            <div>
              <label htmlFor="vehicle" className="block text-sm font-semibold text-gray-700 mb-2">
                {t('booking.vehicle')}
              </label>
              <select
                id="vehicle"
                name="vehicle"
                value={formData.vehicle}
                onChange={handleInputChange}
                required
                className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-yellow-400 focus:border-transparent text-sm pr-8"
              >
                <option value="">{t('booking.select')}</option>
                {vehicles.map((vehicle, index) => (
                  <option key={index} value={vehicle}>{vehicle}</option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              <div>
                <label htmlFor="date" className="block text-sm font-semibold text-gray-700 mb-2">
                  {t('booking.date')}
                </label>
                <input
                  type="date"
                  id="date"
                  name="date"
                  value={formData.date}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-yellow-400 focus:border-transparent text-sm"
                />
              </div>

              <div>
                <label htmlFor="duration" className="block text-sm font-semibold text-gray-700 mb-2">
                  {t('booking.duration')}
                </label>
                <input
                  type="text"
                  id="duration"
                  name="duration"
                  value={formData.duration}
                  onChange={handleInputChange}
                  placeholder={t('booking.days')}
                  required
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-yellow-400 focus:border-transparent text-sm"
                />
              </div>
            </div>

            <div>
              <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                {t('booking.message')}
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                rows={4}
                className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-yellow-400 focus:border-transparent text-sm resize-none"
              ></textarea>
            </div>

            <button
              type="submit"
              className="w-full bg-green-600 text-white py-3 sm:py-4 rounded-xl text-base sm:text-lg font-semibold hover:bg-green-500 transition-colors flex items-center justify-center space-x-2 whitespace-nowrap"
            >
              <i className="ri-whatsapp-line text-lg sm:text-xl"></i>
              <span>{t('booking.submit')}</span>
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default BookingSection;